import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useNavigate } from 'react-router-dom';

function Messages() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(auth.currentUser);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (u) => {
      setUser(u);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const loadConversations = async () => {
      try {
        const q1 = query(collection(db, 'conversations'), where('workerId', '==', user.uid));
        const q2 = query(collection(db, 'conversations'), where('contractorId', '==', user.uid));
        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        const convMap = new Map();
        [...snap1.docs, ...snap2.docs].forEach(doc => {
          convMap.set(doc.id, { id: doc.id, ...doc.data() });
        });
        const convList = [...convMap.values()].sort((a, b) => {
          const aTime = a.lastMessageAt?.toDate?.() || new Date(0);
          const bTime = b.lastMessageAt?.toDate?.() || new Date(0);
          return bTime - aTime;
        });
        setConversations(convList);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    loadConversations();
  }, [user]);

  const getOtherParty = (conv) => {
    if (!user) return '';
    if (conv.workerId === user.uid) return conv.contractorEmail || 'Contractor';
    return conv.workerName || 'Worker';
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    if (status === 'accepted') return '#28a745';
    if (status === 'declined') return '#dc3545';
    return '#ffc107';
  };

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>
          ← Back
        </button>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title}>Messages</h2>
        <p style={styles.subtitle}>Your job conversations</p>

        {loading ? (
          <div style={styles.emptyState}><p>Loading messages...</p></div>
        ) : conversations.length === 0 ? (
          <div style={styles.emptyState}>
            <p style={styles.emptyIcon}>💬</p>
            <p style={styles.emptyTitle}>No messages yet</p>
            <p style={styles.emptyText}>
              Messages will appear here once a job request is sent or received
            </p>
          </div>
        ) : (
          <div style={styles.list}>
            {conversations.map(conv => (
              <div key={conv.id} style={styles.convCard}
                onClick={() => navigate(`/chat/${conv.jobRequestId}`)}>
                <div style={styles.convAvatar}>
                  <span style={styles.convAvatarText}>
                    {getOtherParty(conv).charAt(0).toUpperCase()}
                  </span>
                </div>
                <div style={styles.convInfo}>
                  <div style={styles.convHeader}>
                    <span style={styles.convName}>{getOtherParty(conv)}</span>
                    <span style={styles.convTime}>{formatTime(conv.lastMessageAt)}</span>
                  </div>
                  <p style={styles.convJob}>📋 {conv.jobDescription || 'Job Request'}</p>
                  <div style={styles.convFooter}>
                    <p style={styles.convLastMessage}>
                      {conv.lastMessage || 'No messages yet — tap to start'}
                    </p>
                    <span style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(conv.status) + '20',
                      color: getStatusColor(conv.status),
                    }}>
                      {conv.status?.charAt(0).toUpperCase() + conv.status?.slice(1) || 'Pending'}
                    </span>
                  </div>
                </div>
                <span style={styles.chevron}>›</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  navbar: {
    backgroundColor: 'white', padding: '16px 24px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  backButton: {
    padding: '8px 16px', backgroundColor: 'transparent',
    color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer',
    touchAction: 'manipulation',
  },
  content: { maxWidth: '700px', margin: '0 auto', padding: '24px 16px' },
  title: { fontSize: '26px', color: '#1a1a2e', marginBottom: '8px' },
  subtitle: { color: '#666', marginBottom: '24px' },
  emptyState: { textAlign: 'center', padding: '60px', color: '#666' },
  emptyIcon: { fontSize: '48px', marginBottom: '12px' },
  emptyTitle: { fontSize: '20px', color: '#1a1a2e', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: '#999' },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  convCard: {
    backgroundColor: 'white', borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)', padding: '16px 20px',
    display: 'flex', alignItems: 'center', gap: '14px',
    cursor: 'pointer', touchAction: 'manipulation',
  },
  convAvatar: {
    width: '48px', height: '48px', borderRadius: '50%',
    backgroundColor: '#E94560', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  convAvatarText: { color: 'white', fontSize: '18px', fontWeight: 'bold' },
  convInfo: { flex: 1, minWidth: 0 },
  convHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', marginBottom: '2px',
  },
  convName: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e' },
  convTime: { fontSize: '12px', color: '#999', flexShrink: 0 },
  convJob: { fontSize: '12px', color: '#666', marginBottom: '4px' },
  convFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  convLastMessage: {
    fontSize: '13px', color: '#999',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    maxWidth: '220px',
  },
  statusBadge: {
    fontSize: '11px', fontWeight: 'bold', padding: '2px 8px',
    borderRadius: '10px', flexShrink: 0,
  },
  chevron: { fontSize: '24px', color: '#ccc', flexShrink: 0 },
};

export default Messages;